import { memo, type JSX } from 'react'
import styled from 'styled-components'
import { type Product } from '@/features/shop/services/Product/types'
import { ProductCardName } from '@/features/shop/components/ProductCard/ProductCardName'
import { ProductCardPrice } from '@/features/shop/components/ProductCard/price/ProductCardPrice'
import { ProductCardImage } from '@/features/shop/components/ProductCard/ProductCardImage'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import {
  trackProductClicked,
  trackProductViewed,
  useProductFiltersParams,
} from '@/features/shared/analytics/products'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { NextLink } from '@/features/shared/elements/Link'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'

type Props = {
  product: Product
  index: number
}

export const UngatedProductCard = memo(({ product, index }: Props) => {
  const zip = useMarketingZip()
  const priceAvailable = Boolean(product.price)
  const filterParams = useProductFiltersParams()

  const getProductEventProperties = () => ({
    product,
    displayFormat: DISPLAY_FORMAT.GRID,
    gridIndex: index,
    filterParams,
  })
  const onProductClick = () => trackProductClicked(getProductEventProperties())
  const onProductInView = () => trackProductViewed(getProductEventProperties())

  return (
    <ProductCardWrapper onClick={onProductClick} product={product} zip={zip}>
      <CardWrapperScrollIntoView onInView={onProductInView}>
        <ImageWrapper>
          <ProductCardImage product={product} />
        </ImageWrapper>
        {!!priceAvailable && (
          <SkeletonConsumer height="24px" width="100%">
            <ProductCardPrice product={product} />
          </SkeletonConsumer>
        )}
        <ProductCardName product={product} />
      </CardWrapperScrollIntoView>
    </ProductCardWrapper>
  )
})

type WrapperProps = {
  children: JSX.Element
  product: Product
  zip: string
  onClick: () => void
}
const ProductCardWrapper = ({ children, product, zip, onClick }: WrapperProps) => {
  if (!product.universal_id) {
    return <li>{children}</li>
  }

  const baseUrl = getProductPageUrl({
    universal_id: product.universal_id,
    id: product.id,
    ungated: true,
  })
  const href = zip ? `${baseUrl}?zip=${zip}` : baseUrl

  return (
    <li>
      <NextLink key={product.name} href={href} onClick={onClick}>
        {children}
      </NextLink>
    </li>
  )
}

const CardWrapperScrollIntoView = styled(ScrollIntoView)`
  height: 300px;
  width: 162px;
  cursor: pointer;
`

const ImageWrapper = styled.div`
  border-radius: 8px;
  margin-bottom: 8px;
  min-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 162px;
  height: 162px;
  background-color: ${({ theme }) => theme.white};

  &:hover,
  &:focus,
  &:active {
    box-shadow: ${({ theme }) => theme.shadows[1]};
    transition: box-shadow 0.25s ease;
  }
`
